export const MAX_SEEDED_PLACE_MEDIA_ITEMS = 5;

export function isGooglePlaceMediaUrl(url = "") {
  try {
    const parsed = new URL(String(url || "").trim());
    const host = String(parsed.hostname || "").toLowerCase();
    if (host === "firebasestorage.googleapis.com") return false;
    return (
      host.includes("googleusercontent.com") ||
      host.includes("googleapis.com") ||
      host.includes("gstatic.com")
    );
  } catch {
    return false;
  }
}

export function isGooglePhotoServicePlaceholderUrl(url = "") {
  try {
    const parsed = new URL(String(url || "").trim());
    const host = String(parsed.hostname || "").toLowerCase();
    const path = String(parsed.pathname || "").toLowerCase();
    return host.includes("googleapis.com") && path.includes("/maps/api/place/js/photoservice.getphoto");
  } catch {
    return false;
  }
}

export function isSeededPublicPlaceCapsule(capsule) {
  return (
    capsule?.seeded === true &&
    String(capsule?.capsuleType || "").trim().toLowerCase() === "public"
  );
}

export function limitSeededPlaceMediaItems(media = []) {
  return (Array.isArray(media) ? media : []).filter(Boolean).slice(0, MAX_SEEDED_PLACE_MEDIA_ITEMS);
}

export function shouldAttemptSeededPlaceMediaRepair(capsule, mediaItem) {
  if (!String(capsule?.location?.placeId || "").trim()) return false;
  if (isVideoMediaItem(mediaItem)) return false;
  return isGooglePlaceMediaUrl(String(mediaItem?.url || "").trim());
}

export function shouldHydrateSeededPlaceMediaOnOpen(capsule) {
  if (!String(capsule?.location?.placeId || "").trim()) return false;
  if (!isSeededPublicPlaceCapsule(capsule)) return false;
  if (String(capsule?.seedType || "").trim().toLowerCase() !== "discover") return false;
  const hydrationStatus = String(capsule?.placePhotoHydrationStatus || "").trim().toLowerCase();
  const mediaCount = limitSeededPlaceMediaItems(capsule.media).length;
  if (hydrationStatus === "complete" && mediaCount >= MAX_SEEDED_PLACE_MEDIA_ITEMS) return false;
  if (hydrationStatus === "complete" && mediaCount < MAX_SEEDED_PLACE_MEDIA_ITEMS) return true;
  if (hydrationStatus === "hero_only") return mediaCount < MAX_SEEDED_PLACE_MEDIA_ITEMS;
  const referenceCount = Number(capsule?.placePhotoReferenceCount);
  return mediaCount > 0 && mediaCount < Math.min(
    MAX_SEEDED_PLACE_MEDIA_ITEMS,
    Number.isFinite(referenceCount) && referenceCount > 0 ? referenceCount : MAX_SEEDED_PLACE_MEDIA_ITEMS
  );
}

export function isDirectGooglePlaceImageMediaItem(mediaItem) {
  if (isVideoMediaItem(mediaItem)) return false;
  return isGooglePhotoServicePlaceholderUrl(String(mediaItem?.url || "").trim());
}

function isVideoMediaItem(mediaItem) {
  const type = String(mediaItem?.type || "").trim().toLowerCase();
  return type === "video" || type.startsWith("video/");
}

export function getRenderableMediaItems(media = []) {
  return (Array.isArray(media) ? media : [])
    .map((item, index) => ({ item, originalIndex: index }))
    .filter(({ item }) => !isDirectGooglePlaceImageMediaItem(item));
}

export function getSeededPlaceMediaRepairUrls(capsule) {
  if (!String(capsule?.location?.placeId || "").trim()) return [];

  return limitSeededPlaceMediaItems(capsule.media)
    .filter((item) => shouldAttemptSeededPlaceMediaRepair(capsule, item))
    .map((item) => String(item?.url || "").trim())
    .filter(Boolean);
}

export function withholdRepairableSeededPlaceMedia(capsule) {
  if (!capsule || typeof capsule !== "object") return capsule;
  if (!String(capsule?.location?.placeId || "").trim()) return capsule;

  const media = limitSeededPlaceMediaItems(capsule.media);
  const nextMedia = media.filter((item) => !isDirectGooglePlaceImageMediaItem(item));
  if (nextMedia.length === media.length) return capsule;

  return {
    ...capsule,
    media: nextMedia,
  };
}

export function normalizeSeededPlaceCapsuleMedia(capsule) {
  if (!capsule || typeof capsule !== "object") return capsule;
  if (!isSeededPublicPlaceCapsule(capsule)) return capsule;

  const nextMedia = limitSeededPlaceMediaItems(capsule.media);
  const prevMedia = Array.isArray(capsule.media) ? capsule.media : [];
  if (nextMedia.length === prevMedia.length) {
    return capsule;
  }

  return {
    ...capsule,
    media: nextMedia,
  };
}

export function buildSeededPlacePreviewMedia(photos = []) {
  return limitSeededPlaceMediaItems(
    (Array.isArray(photos) ? photos : []).map((photo) => {
      const url =
        typeof photo?.getUrl === "function"
          ? String(
              photo.getUrl({
                maxWidth: 1280,
              }) || ""
            ).trim()
          : "";
      if (!url) return null;
      return {
        source: "place",
        type: "image",
        sessionPreviewUrl: null,
        storagePath: null,
        url,
      };
    })
  );
}

export function applyRepairedCapsuleMedia(capsule, nextMedia = []) {
  return {
    ...(capsule || {}),
    media: limitSeededPlaceMediaItems(nextMedia),
  };
}
